import { useEffect } from 'react';
import { useLocation } from 'react-router';
import type { AudioGroup, MusicTrack } from './audio.gen';
import { music } from './music';
import { sound } from './sound';

export interface RouteAudio {
  track: MusicTrack;
  /** SFX groups to preload on this screen (loaded only while sound is on) */
  groups: AudioGroup[];
}

const ROUNDS = new Set(['pot', 'roulette', 'hongbao']);
const TEAHOUSE = new Set(['mahjong', 'mahjong-table', 'zhajinhua', 'doudizhu']);
const FESTIVE = new Set(['firecrackers', 'carp']);

/** /:lang/<page>[/<slot>] → music track + SFX groups of the screen. */
export function routeAudio(pathname: string): RouteAudio {
  const [, , page = '', sub = ''] = pathname.split('/');
  if (page === 'crash') return { track: 'crash', groups: ['crash'] };
  if (ROUNDS.has(page)) return { track: 'rounds', groups: [page as AudioGroup] };
  if (page === 'slots' && sub) {
    return { track: sub === 'koi-pond' ? 'koi-pond' : 'slots', groups: ['slots', sub as AudioGroup] };
  }
  if (TEAHOUSE.has(page)) return { track: 'teahouse', groups: [page as AudioGroup] };
  if (FESTIVE.has(page)) return { track: 'festive', groups: [page as AudioGroup] };
  if (page === 'tournaments') return { track: 'lobby', groups: ['tourney'] };
  if (page === 'puzzles' && sub === 'tile-match') return { track: 'teahouse', groups: ['mahjong'] };
  return { track: 'lobby', groups: [] };
}

/** In the shell: the route picks the music track and preloads the screen's SFX group. */
export function useMusicRoute(): void {
  const { pathname } = useLocation();
  useEffect(() => {
    const { track, groups } = routeAudio(pathname);
    music.play(track);
    if (groups.length) sound.preload(...groups);
  }, [pathname]);
  useEffect(() => () => music.stop(), []);
}
